import { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faUndo, faCheck, faSearch, faChevronDown, faTruck, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../ui/ActionButton";
import "../../style/FilterPanel.css";

const ALL = "__ALL__";
const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function MaterialsDropdown({ options, selected, onChange, darkMode, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((m) => m.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (m) => {
    onChange(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  };

  const thm = darkMode ? "fp-dd--dark" : "fp-dd--light";

  return (
    <div className={`fp-dd ${thm}`} ref={ref}>
      <button className="fp-dd__trigger" onClick={() => setIsOpen((v) => !v)} type="button">
        <span className="fp-dd__trigger-text">
          {selected.length === 0 ? t("All materials") : `${selected.length} selected`}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`fp-dd__arrow ${isOpen ? "fp-dd__arrow--open" : ""}`} />
      </button>

      {selected.length > 0 && (
        <div className="fp-dd__selected">
          {selected.map((m) => (
            <span key={m} className="fp-dd__tag">
              {m}
              <button className="fp-dd__tag-x" onClick={() => toggle(m)} type="button">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          <button className="fp-dd__clear" onClick={() => onChange([])} type="button">{t("Clear")}</button>
        </div>
      )}

      {isOpen && (
        <div className="fp-dd__dropdown">
          <div className="fp-dd__search-wrap">
            <FontAwesomeIcon icon={faSearch} className="fp-dd__search-icon" />
            <input
              className="fp-dd__search"
              type="text"
              placeholder={t("Search materials...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="fp-dd__list">
            {filtered.length === 0 && (
              <div className="fp-dd__empty">{t("No results")}</div>
            )}
            {filtered.map((m) => (
              <button key={m} className={`fp-dd__item ${selected.includes(m) ? "fp-dd__item--active" : ""}`}
                onClick={() => toggle(m)} type="button">
                <span className="fp-dd__check">{selected.includes(m) ? "✓" : ""}</span>
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterPanel({
  open,
  onClose,
  initialFilters,
  materialTypes,
  categories,
  users,
  onApply,
  onReset,
  darkMode,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => ({
    ...initialFilters,
    radiusKm: String(initialFilters.radiusKm ?? ""),
  }));

  useEffect(() => {
    if (open) setDraft({
      ...initialFilters,
      radiusKm: String(initialFilters.radiusKm ?? ""),
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));

  const matOpts = useMemo(() => {
    if (!Array.isArray(materialTypes)) return [];
    return materialTypes.map((mt) =>
      typeof mt === "string" ? mt : mt?.value ?? mt?.name ?? mt?.label ?? ""
    ).filter(Boolean);
  }, [materialTypes]);

  const catOpts = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories.map((c) => {
      const val = c?.value ?? c?.label ?? c;
      const label = c?.label ?? c?.value ?? c;
      return { value: val, label };
    }).filter((c) => c.value);
  }, [categories]);

  const locationOpts = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const map = new Map();
    users.forEach((u) => {
      const addr = u?.address?.trim();
      if (addr && !map.has(addr)) {
        map.set(addr, { lat: u.lat ?? null, lon: u.lon ?? null });
      }
    });
    return Array.from(map.entries())
      .map(([addr, coords]) => ({ address: addr, ...coords }))
      .sort((a, b) => a.address.localeCompare(b.address));
  }, [users]);

  const handleApply = () => {
    const parsed = parseFloat(draft.radiusKm);
    const coords = locationOpts.find((l) => l.address === draft.currentLocation);
    onApply({
      ...draft,
      radiusKm: Number.isFinite(parsed) ? parsed : 0,
      locationCoords: coords?.lat != null && coords?.lon != null
        ? { lat: coords.lat, lon: coords.lon }
        : null,
    });
  };
  const handleReset = () => {
    // reset local draft to empty defaults
    setDraft({
      categories: [],
      materials: [],
      quantityMin: "",
      quantityMax: "",
      costMin: "",
      costMax: "",
      pickupLatestBy: "",
      pickupTimeFrom: "",
      pickupTimeTo: "",
      currentLocation: "",
      radiusKm: "2",
      locationCoords: null,
      weekdays: [],
      deliveryMethods: ["pickup", "delivery"],
    });
    onReset();
  };

  // multi-select category toggle
  const toggleCategory = (val) => {
    if (val === ALL) return update("categories", []);
    const cats = Array.isArray(draft.categories) ? [...draft.categories] : [];
    update("categories", cats.includes(val) ? cats.filter((c) => c !== val) : [...cats, val]);
  };

  const themeClass = darkMode ? "fp--dark" : "fp--light";

  if (!open) return null;

  return (
    <aside className={`fp ${themeClass}`}>
      <div className="fp__header">
        <h3 className="fp__title">{t("Filters")}</h3>
        <button className="fp__close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="fp__body">
        {/* row 1 */}
        <div className="fp__group">
          <label className="fp__label">{t("Category")}</label>
          <div className="fp__chips">
            <button className={`fp__chip ${draft.categories.length === 0 ? "fp__chip--active" : ""}`}
              onClick={() => toggleCategory(ALL)}>{t("All")}</button>
            {catOpts.map((c) => (
              <button key={c.value}
                className={`fp__chip ${draft.categories.includes(c.value) ? "fp__chip--active" : ""}`}
                onClick={() => toggleCategory(c.value)}>{t(c.label)}</button>
            ))}
          </div>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Materials")}</label>
          <MaterialsDropdown
            options={matOpts}
            selected={draft.materials}
            onChange={(v) => update("materials", v)}
            darkMode={darkMode}
            t={t}
          />
        </div>

        {/* row 2 */}
        <div className="fp__group">
          <label className="fp__label">{t("Pickup time range")}</label>
          <div className="fp__row">
            <input
              className={`fp__input ${!(draft.pickupTimeFrom || draft.pickupTimeTo) ? "fp__input--faded" : ""}`}
              type="time"
              value={draft.pickupTimeFrom || "09:00"}
              disabled={!(draft.pickupTimeFrom || draft.pickupTimeTo)}
              onChange={(e) => update("pickupTimeFrom", e.target.value)} />
            <span className={`fp__sep ${!(draft.pickupTimeFrom || draft.pickupTimeTo) ? "fp__input--faded" : ""}`}>→</span>
            <input
              className={`fp__input ${!(draft.pickupTimeFrom || draft.pickupTimeTo) ? "fp__input--faded" : ""}`}
              type="time"
              value={draft.pickupTimeTo || "17:00"}
              disabled={!(draft.pickupTimeFrom || draft.pickupTimeTo)}
              onChange={(e) => update("pickupTimeTo", e.target.value)} />
            <label className="fp__checkbox">
              <input type="checkbox"
                checked={!!(draft.pickupTimeFrom || draft.pickupTimeTo)}
                onChange={(e) => {
                  if (e.target.checked) {
                    update("pickupTimeFrom", "09:00");
                    update("pickupTimeTo", "17:00");
                  } else {
                    update("pickupTimeFrom", "");
                    update("pickupTimeTo", "");
                  }
                }}
              />
              <span>{t("Custom time")}</span>
            </label>
          </div>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Price range (CAD)")}</label>
          <div className="fp__row">
            <input className="fp__input" type="text" inputMode="decimal" placeholder="0"
              value={draft.costMin} onChange={(e) => update("costMin", e.target.value)} />
            <span className="fp__sep">→</span>
            <input className="fp__input" type="text" inputMode="decimal" placeholder="25"
              value={draft.costMax} onChange={(e) => update("costMax", e.target.value)} />
          </div>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Quantity (lbs)")}</label>
          <div className="fp__row">
            <input className="fp__input" type="text" inputMode="numeric" placeholder="0"
              value={draft.quantityMin} onChange={(e) => update("quantityMin", e.target.value)} />
            <span className="fp__sep">→</span>
            <input className="fp__input" type="text" inputMode="numeric" placeholder="1000"
              value={draft.quantityMax} onChange={(e) => update("quantityMax", e.target.value)} />
          </div>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Latest pickup by")}</label>
          <input className="fp__input fp__input--full" type="date"
            value={draft.pickupLatestBy} onChange={(e) => update("pickupLatestBy", e.target.value)} />
        </div>

        {/* row 3 */}
        <div className="fp__group">
          <label className="fp__label">{t("Receiving method & schedule")}</label>
          <div className="fp__row-pair">
            <div className="fp__methods">
              {[
                { key: "pickup", label: t("Pickup"), icon: faBoxOpen, cls: "fp__method--pickup" },
                { key: "delivery", label: t("Delivery"), icon: faTruck, cls: "fp__method--delivery" },
              ].map(({ key, label, icon, cls }) => {
                const methods = Array.isArray(draft.deliveryMethods) ? draft.deliveryMethods : ["pickup", "delivery"];
                const isOn = methods.includes(key);
                return (
                  <button key={key}
                    className={`fp__method ${cls} ${isOn ? "fp__method--active" : ""}`}
                    onClick={() => {
                      const next = isOn ? methods.filter((m) => m !== key) : [...methods, key];
                      if (next.length === 0) return;
                      update("deliveryMethods", next);
                    }}>
                    <FontAwesomeIcon icon={icon} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
            <div className="fp__days">
              {DAYS.map((label, i) => {
                const val = String(i);
                const isOn = Array.isArray(draft.weekdays) && draft.weekdays.includes(val);
                return (
                  <span key={i}
                    className={`fp__day ${isOn ? "fp__day--active" : ""}`}
                    onClick={() => {
                      const wd = Array.isArray(draft.weekdays) ? [...draft.weekdays] : [];
                      update("weekdays", isOn ? wd.filter((d) => d !== val) : [...wd, val]);
                    }}>{label}</span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Location")}</label>
          <select className="fp__select fp__select--single" value={draft.currentLocation}
            onChange={(e) => update("currentLocation", e.target.value)}>
            <option value="">{t("All locations")}</option>
            {locationOpts.map((l) => {
              const hasCoords = Number.isFinite(Number(l.lat)) && Number.isFinite(Number(l.lon));
              return (
                <option key={l.address} value={l.address}>
                  {l.address}{hasCoords ? "" : " (no map data)"}
                </option>
              );
            })}
          </select>
        </div>

        <div className="fp__group">
          <label className="fp__label">{t("Radius (km)")}</label>
          <input className="fp__input fp__input--full" type="text" inputMode="decimal"
            placeholder="0"
            value={draft.radiusKm}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d*$/.test(v)) update("radiusKm", v);
            }} />
        </div>
      </div>

      <div className="fp__actions">
        <ActionButton variant="secondary" icon={faCheck} className="fp__apply" onClick={handleApply}>{t("Apply")}</ActionButton>
        <ActionButton variant="default" icon={faUndo} iconOnly tooltip={t("Reset filters")} onClick={handleReset} />
      </div>
    </aside>
  );
}

FilterPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialFilters: PropTypes.object,
  materialTypes: PropTypes.array,
  categories: PropTypes.array,
  users: PropTypes.array,
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  darkMode: PropTypes.bool,
};

export default FilterPanel;
